import React, { FC, useContext } from "react";
import { Box, GridLegacy as Grid, Typography } from "@mui/material";
import EditableGroup, { EditableGroupType } from "@/components/editable/EditableGroup/EditableGroup";
import EditableField, { EditableFieldTypes } from "@/components/editable/EditableField/EditableField";
import { IPreferences, IShuttlePickupFieldOptions } from "@/logic/api";
import { SessionContext } from "@/utility/useSession";
import { isSysAdmin } from "@/utility/utils";
import { useTranslation } from "react-i18next";

interface ShuttlePickupRuleOptionsProps {
    onSave: (data: Partial<IPreferences>) => Promise<boolean>;
    useLegacyLayout?: boolean;
}

export const ShuttlePickupRuleOptions: FC<ShuttlePickupRuleOptionsProps> = ({
    onSave,
    useLegacyLayout = false
}) => {
    const session = useContext(SessionContext);
    const { t } = useTranslation();

    if (!session.current.site || !isSysAdmin(session)) {
        return null;
    }

    const ensureFields = (data: IPreferences): IShuttlePickupFieldOptions => {
        if (!data.shuttlePickupFieldOptions?.properties) {
            data.shuttlePickupFieldOptions = { properties: {} };
        }
        return data.shuttlePickupFieldOptions;
    };

    const editableGroup = (
        <EditableGroup<IPreferences>
            id="shuttlePickupFieldOptions"
            editType={EditableGroupType.switchableEditing}
            data={session.current.site.preferences}
            onDataChanged={(data) => onSave({
                shuttlePickupFieldOptions: data.shuttlePickupFieldOptions
            })}
            testId="shuttle-pickup-fields"
        >
            {useLegacyLayout && (
                <Typography variant="subtitle1" fontWeight="bold">
                    {t("settings.shuttlePickup.fieldOptions")}
                </Typography>
            )}
            <Grid container spacing={useLegacyLayout ? 0 : 2}>
                <Grid item sm={4} xs={useLegacyLayout ? 6 : 12}>
                    <EditableField<IPreferences, boolean>
                        useFullWidthWhenEditing={false}
                        inputType={EditableFieldTypes.switch}
                        label={t("settings.shuttlePickup.enableExpectedReturn")}
                        onProvide={(data) => Boolean(data.shuttlePickupFieldOptions?.properties?.enableExpectedReturn?.required)}
                        onChange={(data, value) => ensureFields(data).properties.enableExpectedReturn = { required: value }}
                        testId="settings-shuttle-pickup-expected-return-switch"
                    />
                    <EditableField<IPreferences, boolean>
                        useFullWidthWhenEditing={false}
                        inputType={EditableFieldTypes.switch}
                        label={t("settings.shuttlePickup.enableRequestReceived")}
                        onProvide={(data) => Boolean(data.shuttlePickupFieldOptions?.properties?.enableRequestReceived?.required)}
                        onChange={(data, value) => ensureFields(data).properties.enableRequestReceived = { required: value }}
                        testId="settings-shuttle-pickup-request-received-switch"
                    />
                    <EditableField<IPreferences, boolean>
                        useFullWidthWhenEditing={false}
                        inputType={EditableFieldTypes.switch}
                        label={t("settings.shuttlePickup.enableCustomerName")}
                        onProvide={(data) => Boolean(data.shuttlePickupFieldOptions?.properties?.enableCustomerName?.required)}
                        onChange={(data, value) => ensureFields(data).properties.enableCustomerName = { required: value }}
                        testId="settings-shuttle-pickup-customer-name-switch"
                    />
                </Grid>
                <Grid item sm={4} xs={useLegacyLayout ? 6 : 12}>
                    <EditableField<IPreferences, boolean>
                        useFullWidthWhenEditing={false}
                        inputType={EditableFieldTypes.switch}
                        label={t("settings.shuttlePickup.enablePAX")}
                        onProvide={(data) => Boolean(data.shuttlePickupFieldOptions?.properties?.enablePAX?.required)}
                        onChange={(data, value) => ensureFields(data).properties.enablePAX = { required: value }}
                        testId="settings-shuttle-pickup-pax-switch"
                    />
                    <EditableField<IPreferences, boolean>
                        useFullWidthWhenEditing={false}
                        inputType={EditableFieldTypes.switch}
                        label={t("settings.shuttlePickup.enableReturnFlight")}
                        onProvide={(data) => Boolean(data.shuttlePickupFieldOptions?.properties?.enableReturnFlight?.required)}
                        onChange={(data, value) => ensureFields(data).properties.enableReturnFlight = { required: value }}
                        testId="settings-shuttle-pickup-return-flight-switch"
                    />
                    <EditableField<IPreferences, boolean>
                        useFullWidthWhenEditing={false}
                        inputType={EditableFieldTypes.switch}
                        label={t("settings.shuttlePickup.enableReturningFrom")}
                        onProvide={(data) => Boolean(data.shuttlePickupFieldOptions?.properties?.enableReturningFrom?.required)}
                        onChange={(data, value) => ensureFields(data).properties.enableReturningFrom = { required: value }}
                        testId="settings-shuttle-pickup-returning-from-switch"
                    />
                </Grid>
                <Grid item sm={4} xs={useLegacyLayout ? 6 : 12}>
                    <EditableField<IPreferences, boolean>
                        useFullWidthWhenEditing={false}
                        inputType={EditableFieldTypes.switch}
                        label={t("settings.shuttlePickup.enableChildSeats")}
                        onProvide={(data) => Boolean(data.shuttlePickupFieldOptions?.properties?.enableChildSeats?.required)}
                        onChange={(data, value) => ensureFields(data).properties.enableChildSeats = { required: value }}
                        testId="settings-shuttle-pickup-child-seats-switch"
                    />
                    <EditableField<IPreferences, boolean>
                        useFullWidthWhenEditing={false}
                        inputType={EditableFieldTypes.switch}
                        label={t("settings.shuttlePickup.enableOversizeBaggage")}
                        onProvide={(data) => Boolean(data.shuttlePickupFieldOptions?.properties?.enableOversizeBaggage?.required)}
                        onChange={(data, value) => ensureFields(data).properties.enableOversizeBaggage = { required: value }}
                        testId="settings-shuttle-pickup-oversize-baggage-switch"
                    />
                    <EditableField<IPreferences, boolean>
                        useFullWidthWhenEditing={false}
                        inputType={EditableFieldTypes.switch}
                        label={t("settings.shuttlePickup.enableWheelChairUser")}
                        onProvide={(data) => Boolean(data.shuttlePickupFieldOptions?.properties?.enableWheelChairUser?.required)}
                        onChange={(data, value) => ensureFields(data).properties.enableWheelChairUser = { required: value }}
                        testId="settings-shuttle-pickup-wheelchair-user-switch"
                    />
                </Grid>
            </Grid>
        </EditableGroup>
    );

    if (useLegacyLayout) {
        return editableGroup;
    }

    return (
        <Box sx={{ mb: 4 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: "bold", mb: 1.5 }}>
                {t("settings.shuttlePickup.fieldOptions")}
            </Typography>
            <Box
                data-testid="shuttle-pickup-rule-options"
                sx={{
                    p: 2,
                    mb: 2,
                    border: "1px solid",
                    borderColor: "divider",
                    borderRadius: 1,
                    bgcolor: "background.paper",
                    position: "relative",
                    isolation: "isolate",
                }}
            >
                {editableGroup}
            </Box>
        </Box>
    );
};
